<template>
  <div class="cards-grid">
    <div
      v-for="item in orders"
      :key="item.pedidoId"
      class="order-card"
      @click="$emit('view-details', item)"
    >
      <div class="order-card-header">
        <div class="order-id-group">
          <span class="order-id">{{ item.pedidoNumeroCliente || '-' }}</span>
          <span class="order-erp" v-if="item.pedidoNumeroIntegracao">
            ERP: {{ item.pedidoNumeroIntegracao }}
          </span>
        </div>
        <StatusBadge :status="item.pedidoSitSales || item.pedidoSitComercial" />
      </div>

      <div class="order-card-body">
        <div class="info-row client-row">
          <feather-icon icon="UserIcon" size="16" class="info-icon" />
          <div class="info-content">
            <span class="info-label">Cliente</span>
            <span class="info-value">{{ formatCliente(item) }}</span>
          </div>
        </div>

        <div class="info-row" v-if="item.representantenome">
          <feather-icon icon="BriefcaseIcon" size="16" class="info-icon" />
          <div class="info-content">
            <span class="info-label">Representante</span>
            <span class="info-value">{{ item.representantenome }}</span>
          </div>
        </div>

        <div class="info-grid">
          <div class="info-cell">
            <span class="cell-label">Emissão</span>
            <span class="cell-value">{{ item.pedidoData || '-' }}</span>
          </div>
          <div class="info-cell">
            <span class="cell-label">Prev. Entrega</span>
            <span class="cell-value">{{ item.pedidoDataEntregaPrevista || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="order-card-footer">
        <div class="footer-value">
          <span class="value-label">Valor Total</span>
          <span class="value-amount">{{ formatValorComImpostos(item) }}</span>
        </div>
        <div class="footer-actions">
          <button
            class="card-btn card-btn-view"
            @click.stop="$emit('view-details', item)"
            title="Ver Detalhes"
          >
            <feather-icon icon="EyeIcon" size="16" />
          </button>
          <button
            class="card-btn card-btn-pdf"
            @click.stop="$emit('generate-pdf', item)"
            title="Gerar PDF"
          >
            <feather-icon icon="FileTextIcon" size="16" />
          </button>
        </div>
      </div>
    </div>

    <div v-if="orders.length === 0" class="empty-state">
      <feather-icon icon="PackageIcon" size="48" />
      <p class="empty-title">Nenhum pedido encontrado</p>
      <p class="empty-subtitle">Ajuste os filtros ou crie um novo pedido</p>
    </div>
  </div>
</template>

<script>
import StatusBadge from './StatusBadge.vue';

export default {
  name: 'CardsView',
  components: {
    StatusBadge,
  },
  props: {
    orders: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    formatCliente(item) {
      const id = item.clienteIdERP ? String(item.clienteIdERP) : '';
      const nome = item.clienteRazaoSocial || 'Não informado';
      return id ? `${id} - ${nome}` : nome;
    },
    formatValorComImpostos(item) {
      if (item.pedidototalcomimpostos !== null && item.pedidototalcomimpostos !== undefined && item.pedidototalcomimpostos !== '') {
        const valor = Number(item.pedidototalcomimpostos);
        return Number.isNaN(valor) ? item.pedidototalcomimpostos : this.formatCurrency(valor);
      }
      return item.pedidoValorTotal || 'R$ 0,00';
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return 'R$ 0,00';
      const num = Number(value);
      if (isNaN(num)) return 'R$ 0,00';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(num);
    },
  },
};
</script>

<style scoped>
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 1rem;
}

.order-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.order-card:hover {
  border-color: var(--primary, #7c3aed);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.order-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem;
  background: var(--surface-bg, #f8fafc);
  border-bottom: 1px solid var(--border-color, #e5e7eb);
}

.order-id-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.order-id {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary, #1e293b);
}

.order-erp {
  font-size: 0.75rem;
  color: var(--text-muted, #94a3b8);
  font-family: monospace;
}

.order-card-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.info-icon {
  color: var(--text-muted, #94a3b8);
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.info-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted, #94a3b8);
}

.info-value {
  font-size: 0.875rem;
  color: var(--text-primary, #1e293b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.client-row .info-value {
  font-weight: 500;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--border-color, #e5e7eb);
}

.info-cell {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.cell-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted, #94a3b8);
}

.cell-value {
  font-size: 0.8125rem;
  color: var(--text-secondary, #64748b);
}

.order-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  background: var(--surface-bg, #f8fafc);
  border-top: 1px solid var(--border-color, #e5e7eb);
}

.footer-value {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.value-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted, #94a3b8);
}

.value-amount {
  font-size: 1rem;
  font-weight: 700;
  color: var(--primary, #7c3aed);
}

.footer-actions {
  display: flex;
  gap: 0.5rem;
}

.card-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.card-btn-view {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.card-btn-view:hover {
  background: rgba(59, 130, 246, 0.2);
}

.card-btn-pdf {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.card-btn-pdf:hover {
  background: rgba(16, 185, 129, 0.2);
}

.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-muted, #94a3b8);
}

.empty-title {
  margin: 1rem 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
}

.empty-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary, #64748b);
}

@media (max-width: 1200px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

body.dark-layout .order-card {
  background: var(--dark-card-bg, #283046);
  border-color: var(--dark-border, #3b4253);
}

body.dark-layout .order-card-header,
body.dark-layout .order-card-footer {
  background: var(--dark-surface-bg, #242b3d);
  border-color: var(--dark-border, #3b4253);
}

body.dark-layout .order-id,
body.dark-layout .info-value {
  color: var(--dark-text-primary, #d0d2d6);
}

body.dark-layout .info-grid {
  border-color: var(--dark-border, #3b4253);
}

body.dark-layout .empty-title {
  color: var(--dark-text-primary, #d0d2d6);
}

body.dark-layout .empty-subtitle {
  color: var(--dark-text-secondary, #b4b7bd);
}
</style>
